import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { ENTITY_TYPES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Star, CheckCircle, PaperPlaneTilt, Clock, MinusCircle, Heart } from "@phosphor-icons/react";

// Colour + label lookup for the client portal-access status pill. Backend sets
// portal_status per row in GET /api/clients based on the client's login user +
// pending portal_invite reset token.
const PORTAL_STATUS = {
  activated: { label: "Activated", classes: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle },
  invited: { label: "Invited", classes: "bg-indigo-50 text-indigo-800 border-indigo-200", Icon: PaperPlaneTilt },
  never_signed_in: { label: "Never signed in", classes: "bg-amber-50 text-amber-800 border-amber-200", Icon: Clock },
  no_login: { label: "No login", classes: "bg-zinc-100 text-zinc-500 border-zinc-200", Icon: MinusCircle },
};

// Health score → colour band. Backend returns health_score (0-100) and
// health_breakdown per client. Bands mirror lightspeed status conventions.
function healthBand(score) {
  if (score >= 80) return { label: "Healthy", classes: "bg-emerald-50 text-emerald-800 border-emerald-200 ring-emerald-200" };
  if (score >= 60) return { label: "Steady", classes: "bg-sky-50 text-sky-800 border-sky-200 ring-sky-200" };
  if (score >= 40) return { label: "Watch", classes: "bg-amber-50 text-amber-800 border-amber-200 ring-amber-200" };
  return { label: "At risk", classes: "bg-rose-50 text-rose-800 border-rose-200 ring-rose-200" };
}

export default function Clients() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", entity_type: "Individual", email: "", phone: "", states: "TX", tx_franchise: false, send_invite: true });
  const { data: clients } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });

  const create = useMutation({
    mutationFn: async () => {
      const { send_invite, email, name, ...rest } = form;
      // Step 1: create the client profile row.
      const { data: created } = await api.post("/clients", {
        ...rest, name, email,
        states: form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
      });
      // Step 2: if requested + we have an email, immediately chain the portal
      // activation invite so the client actually receives an email. Without
      // this, POST /clients alone creates a silent profile with no email.
      let invited = false;
      let invite_error = null;
      if (send_invite && email && email.trim()) {
        try {
          await api.post(`/clients/${created.id}/invite`, { email: email.trim(), name });
          invited = true;
        } catch (e) {
          invite_error = errText(e);
        }
      }
      return { client: created, invited, invite_error };
    },
    onSuccess: ({ client, invited, invite_error }) => {
      if (invited) {
        toast.success(`Client created — activation link emailed to ${client.email}`);
      } else if (invite_error) {
        // The client row was created but the email failed. Tell the user
        // exactly why so they can fix (usually: RESEND_API_KEY missing in prod
        // Deployment Settings, or unverified sending domain).
        toast.warning(`Client created — but email could NOT be sent: ${invite_error}. Fix and click "Invite portal access" from the client detail page.`);
      } else {
        toast.success("Client created");
      }
      setOpen(false);
      setForm({ name: "", entity_type: "Individual", email: "", phone: "", states: "TX", tx_franchise: false, send_invite: true });
      qc.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Clients</h1>
          <p className="text-sm text-zinc-500 mt-1">{clients?.length || 0} records · individuals & entities</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" size="sm"><Plus size={14} className="mr-1" /> New client</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New client</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="client-name-input" placeholder="Client / entity name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                <SelectTrigger data-testid="client-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="client-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="client-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="client-states-input" placeholder="States (comma-separated, e.g. TX, CA)" value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input data-testid="client-txfranchise-checkbox" type="checkbox" checked={form.tx_franchise} onChange={(e) => setForm({ ...form, tx_franchise: e.target.checked })} />
                Texas Franchise Tax filer
              </label>
              <label className={`flex items-start gap-2 text-sm rounded-md border px-3 py-2 ${form.email && form.email.trim() ? "border-indigo-200 bg-indigo-50/50 text-zinc-800" : "border-zinc-200 bg-zinc-50 text-zinc-400"}`}>
                <input
                  data-testid="client-send-invite-checkbox"
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.send_invite && !!form.email.trim()}
                  disabled={!form.email.trim()}
                  onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                />
                <span className="leading-snug">
                  <span className="block font-medium">Send portal invite email now</span>
                  <span className="block text-xs text-zinc-500 mt-0.5">
                    {form.email.trim()
                      ? "Emails an activation link to the address above so the client can set their password and sign in."
                      : "Add an email above to send an activation link on create."}
                  </span>
                </span>
              </label>
              <Button data-testid="client-save-btn" className="w-full" disabled={!form.name || create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? "Creating…" : (form.send_invite && form.email.trim() ? "Create client & send invite" : "Create client")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs">Name</TableHead>
              <TableHead className="text-xs">Entity</TableHead>
              <TableHead className="text-xs">Email</TableHead>
              <TableHead className="text-xs">Portal access</TableHead>
              <TableHead className="text-xs">Health</TableHead>
              <TableHead className="text-xs">States</TableHead>
              <TableHead className="text-xs">TX Franchise</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(clients || []).map((c) => {
              const s = PORTAL_STATUS[c.portal_status] || PORTAL_STATUS.no_login;
              const StatusIcon = s.Icon;
              const score = typeof c.health_score === "number" ? c.health_score : null;
              const band = score !== null ? healthBand(score) : null;
              const bd = c.health_breakdown || {};
              const tooltip = score !== null
                ? `Portal ${bd.portal ?? 0}/25 · Docs ${bd.docs ?? 0}/25 · Engagement ${bd.engagement ?? 0}/25 · Payments ${bd.payments ?? 0}/25`
                : "";
              return (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-zinc-50 transition-colors duration-150">
                  <TableCell><Link to={`/clients/${c.id}`} data-testid={`client-link-${c.id}`} className="text-sm font-medium text-zinc-900 hover:underline">{c.name}</Link></TableCell>
                  <TableCell><span className="text-xs bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">{c.entity_type}</span></TableCell>
                  <TableCell className="text-sm text-zinc-500">{c.email}</TableCell>
                  <TableCell>
                    <span
                      data-testid={`client-portal-status-${c.id}`}
                      title={c.portal_last_login_at ? `Last sign-in ${new Date(c.portal_last_login_at).toLocaleString()}` : s.label}
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${s.classes}`}>
                      <StatusIcon size={10} weight="fill" /> {s.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    {band && (
                      <span
                        data-testid={`client-health-score-${c.id}`}
                        title={tooltip}
                        className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${band.classes}`}>
                        <Heart size={10} weight="fill" />
                        <span className="font-mono tabular-nums">{score}</span>
                        <span className="text-[10px] opacity-80">{band.label}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-zinc-600">{(c.states || []).join(", ")}</TableCell>
                  <TableCell>{c.tx_franchise && <Star size={14} weight="fill" className="text-amber-500" />}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
